import { useState } from 'react';
import { StagedFile } from '@/types/assetGrouping';

export function useStagedFiles() {
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);

  const addFiles = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    
    const newStagedFiles: StagedFile[] = fileArray.map(file => {
      const isVideo = file.type.startsWith('video/');
      const preview_url = URL.createObjectURL(file);
      
      return {
        id: `${file.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        name: file.name,
        size: file.size,
        dimensions: 'Loading...',
        type: isVideo ? 'video' : 'image',
        preview_url,
        selected: true
      };
    });

    setStagedFiles(prev => [...prev, ...newStagedFiles]);

    // Update dimensions asynchronously
    newStagedFiles.forEach(stagedFile => {
      if (stagedFile.type === 'image') {
        const img = new window.Image();
        img.onload = () => {
          setStagedFiles(prev => 
            prev.map(f => 
              f.id === stagedFile.id 
                ? { ...f, dimensions: `${img.width}x${img.height}` }
                : f
            )
          );
        };
        img.src = stagedFile.preview_url;
      } else {
        const video = document.createElement('video');
        video.onloadedmetadata = () => {
          setStagedFiles(prev => 
            prev.map(f => 
              f.id === stagedFile.id 
                ? { ...f, dimensions: `${video.videoWidth}x${video.videoHeight}` }
                : f
            )
          );
        };
        video.src = stagedFile.preview_url;
      }
    });
  };

  const removeFile = (id: string) => {
    setStagedFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file) URL.revokeObjectURL(file.preview_url);
      return prev.filter(f => f.id !== id);
    });
  };

  const toggleSelect = (id: string) => {
    setStagedFiles(prev => 
      prev.map(f => f.id === id ? { ...f, selected: !f.selected } : f)
    );
  };

  const selectAll = () => setStagedFiles(prev => prev.map(f => ({ ...f, selected: true })));
  const deselectAll = () => setStagedFiles(prev => prev.map(f => ({ ...f, selected: false })));
  const clearAll = () => {
    stagedFiles.forEach(f => URL.revokeObjectURL(f.preview_url));
    setStagedFiles([]);
  };

  return {
    stagedFiles,
    addFiles,
    removeFile,
    toggleSelect,
    selectAll,
    deselectAll,
    clearAll,
    selectedCount: stagedFiles.filter(f => f.selected).length,
    totalCount: stagedFiles.length
  };
}
